import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { getStoredAuthToken, setStoredAuthToken } from "../api/api";

const AUTH_USER_KEY = "stihiya_auth_user";

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = window.sessionStorage.getItem(AUTH_USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
}

function writeStoredUser(user) {
  try {
    if (user) {
      window.sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    } else {
      window.sessionStorage.removeItem(AUTH_USER_KEY);
    }
  } catch (err) {
    // ignore storage failures
  }
}

export function AuthProvider({ children }) {
  const [authUser, setAuthUser] = useState(() => readStoredUser());
  const [authToken, setAuthToken] = useState(() => getStoredAuthToken());

  const setAuth = useCallback((user, token) => {
    const nextUser = user ?? null;
    const nextToken = token ?? null;

    setAuthUser(nextUser);
    setAuthToken(nextToken);
    writeStoredUser(nextUser);
    setStoredAuthToken(nextToken);
  }, []);

  const clearAuth = useCallback(() => {
    setAuth(null, null);
  }, [setAuth]);

  const value = useMemo(
    () => ({ authUser, authToken, setAuth, clearAuth }),
    [authUser, authToken, setAuth, clearAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
